"use client"

import * as React from "react"
import {
    ColumnDef,
} from "@tanstack/react-table"
import { MoreVerticalIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import CommonList from "@/components/common/List"
import CommonTableList from "@/components/common/TableList"
import FixedRouteEdit from "./Edit"
import FixedRouteCreate from "./Create"
import PerimeterDialogDraw from "./PerimeterDialogDraw"
import CommonDelete from "@/components/common/Delete"

export type Order = {
    id: number
    client_name: string
    client_phone: string
    pickup: string
    dropoff: string
    status: "NEW_ORDER" | "IN_PROGRESS" | "ASSIGNED" | "ACCEPTED" | "REJECTED" | "CANCELLED" | "COMPLETED"
    created_at: string
}

export const FixedRoutesList = () => {
    const columns = React.useMemo<ColumnDef<Order>[]>(() => [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("id")}</div>
            ),
        },
        {
            accessorKey: "origin",
            header: "Origin",
            cell: ({ row }) => {
                const pickup: string = row.getValue("origin");
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{pickup}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "destination",
            header: "Destination",
            cell: ({ row }) => {
                const pickup: string = row.getValue("destination");
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{pickup}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "price",
            header: "Price",
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{row.getValue("price")}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "currency",
            header: "Currency",
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{row.getValue("currency")}</span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            cell: ({row}) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                            size="icon"
                        >
                            <MoreVerticalIcon />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                        <FixedRouteEdit prop={row.original.id}/>
                        <DropdownMenuSeparator />
                        <CommonDelete title="Delete" id={row.original.id} resource="fixed-routes" responseMessage="Route deleted"/>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        }
    ], []);

    return (
        <CommonList>
            <CommonTableList
                columns={columns}
                create={<FixedRouteCreate />}
                anotherButton={<PerimeterDialogDraw />}
                filterSearch="destination"
            />
        </CommonList>
    )
}

export default FixedRoutesList;